import React from 'react';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  Layers,
  ArrowLeftRight,
  ListOrdered,
  Tag,
  Zap,
  ShieldAlert,
  BarChart3,
  Palette,
  Sliders,
  LogOut,
  User,
} from 'lucide-react';
import { ROUTES, NavigationTab } from '@/lib/constants';
import { useAuth } from '@/services/auth/authContext';

interface NavItem {
  id: NavigationTab;
  label: string;
  icon: React.ElementType;
  badge?: number | string;
}

interface SidebarProps {
  activeTab: NavigationTab;
  onSelectTab: (tab: NavigationTab) => void;
  openPositionsCount?: number;
  unacknowledgedSignalsCount?: number;
  className?: string;
}

export const Sidebar: React.FC<SidebarProps> = ({
  activeTab,
  onSelectTab,
  openPositionsCount = 0,
  unacknowledgedSignalsCount = 0,
  className,
}) => {
  const { username, logout } = useAuth();

  const navItems: NavItem[] = [
    {
      id: ROUTES.OVERVIEW,
      label: 'Overview',
      icon: LayoutDashboard,
    },
    {
      id: ROUTES.POSITIONS,
      label: 'Open Positions',
      icon: Layers,
      badge: openPositionsCount > 0 ? openPositionsCount : undefined,
    },
    {
      id: ROUTES.TRADES,
      label: 'Executions & Trades',
      icon: ArrowLeftRight,
    },
    {
      id: ROUTES.ORDERS,
      label: 'Order Book',
      icon: ListOrdered,
    },
    {
      id: ROUTES.HISTORY,
      label: 'Buy / Sell History',
      icon: Tag,
    },
    {
      id: ROUTES.SIGNALS,
      label: 'Signals & Events',
      icon: Zap,
      badge: unacknowledgedSignalsCount > 0 ? unacknowledgedSignalsCount : undefined,
    },
    {
      id: ROUTES.RISK_HEALTH,
      label: 'Risk & System Health',
      icon: ShieldAlert,
    },
    {
      id: ROUTES.ANALYTICS,
      label: 'P&L Analytics',
      icon: BarChart3,
    },
    {
      id: ROUTES.THEMES,
      label: 'Themes',
      icon: Palette,
    },
    {
      id: ROUTES.SETTINGS,
      label: 'Runtime Settings',
      icon: Sliders,
    },
  ];

  return (
    <aside
      className={cn(
        'w-64 border-r border-[var(--border-default)] bg-[var(--sidebar-bg)] flex flex-col justify-between p-3 select-none',
        className
      )}
      aria-label="Sidebar Navigation"
    >
      <div className="space-y-6">
        {/* Navigation Category */}
        <div className="space-y-1">
          <div className="px-3 py-1.5 text-[10px] font-mono font-bold tracking-wider text-[var(--text-dim)] uppercase">
            Navigation
          </div>
          <nav className="space-y-1" aria-label="Main Navigation">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;

              return (
                <button
                  key={item.id}
                  onClick={() => onSelectTab(item.id)}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    'w-full relative flex items-center justify-between px-3 py-2 rounded-md text-xs font-mono font-medium transition-all duration-150 ease-out group focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] focus-visible:outline-none active:scale-[0.99] cursor-pointer',
                    isActive
                      ? 'bg-[var(--accent-muted)] text-[var(--text-primary)] border border-[var(--accent-primary)]/40 shadow-[0_0_12px_var(--accent-muted)] font-semibold'
                      : 'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-elevated)] border border-transparent'
                  )}
                >
                  {isActive && (
                    <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 bg-[var(--accent-primary)] rounded-r shadow-[0_0_8px_var(--accent-primary)]" />
                  )}
                  <div className="flex items-center gap-2.5">
                    <Icon
                      className={cn(
                        'w-4 h-4 transition-colors duration-150',
                        isActive
                          ? 'text-[var(--accent-primary)]'
                          : 'text-[var(--text-dim)] group-hover:text-[var(--text-primary)]'
                      )}
                      aria-hidden="true"
                    />
                    <span>{item.label}</span>
                  </div>

                  {item.badge !== undefined && (
                    <span
                      className={cn(
                        'px-1.5 py-0.5 rounded text-[10px] font-mono font-bold tabular-nums transition-colors duration-150',
                        isActive
                          ? 'bg-[var(--accent-primary)] text-white'
                          : 'bg-[var(--bg-surface-elevated)] text-[var(--text-muted)] group-hover:bg-[var(--bg-surface-muted)]'
                      )}
                      aria-label={`${item.badge} items`}
                    >
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Read-Only Safety Notice */}
        <div className="p-3 rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface-elevated)]/60 text-[11px] font-mono text-[var(--text-muted)] space-y-1">
          <div className="flex items-center gap-1 text-[var(--accent-secondary)] font-semibold">
            <span>OBSERVABILITY & CONTROL</span>
          </div>
          <p className="text-[10px] text-[var(--text-dim)] leading-relaxed">
            Order executions run autonomously on Oracle VM. Runtime settings and credentials can be managed via authorized session.
          </p>
        </div>
      </div>

      {/* Operator Session & Logout Area */}
      <div className="space-y-3 pt-3 border-t border-[var(--border-default)]">
        <div className="flex items-center justify-between px-2 py-1.5 rounded-lg bg-[var(--bg-surface-elevated)]/60 border border-[var(--border-default)] text-xs font-mono">
          <div className="flex items-center gap-2 overflow-hidden">
            <div className="p-1 rounded bg-[var(--bg-surface)] text-[var(--accent-primary)]">
              <User className="w-3.5 h-3.5" />
            </div>
            <span className="truncate text-[var(--text-secondary)] font-medium" title={username || 'Operator'}>
              {username || 'Operator'}
            </span>
          </div>
          <button
            onClick={() => logout()}
            title="Log out of operator session"
            className="p-1.5 rounded text-[var(--text-muted)] hover:text-rose-400 hover:bg-rose-950/40 transition-colors active:scale-95 cursor-pointer"
            aria-label="Logout"
          >
            <LogOut className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* System info badge */}
        <div className="text-[10px] font-mono text-[var(--text-dim)] flex items-center justify-between px-2">
          <span>v1.0.0 (SPA)</span>
          <span className="text-[var(--accent-primary)] flex items-center gap-1 font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent-primary)]" />
            READY
          </span>
        </div>
      </div>
    </aside>
  );
};
